import React from 'react';

/**
 * Device detection utility for responsive behavior
 */

export interface DeviceInfo {
	isTablet: boolean;
	isMobile: boolean;
	isTouch: boolean;
	screenWidth: number;
	screenHeight: number;
}

/**
 * Detects if the current device is a tablet
 * Considers touch capability and screen size (768px - 1366px)
 */
export const detectDevice = (): DeviceInfo => {
	const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
	const screenWidth = window.innerWidth;
	const screenHeight = window.innerHeight;

	// Tablet: touch-enabled device with width between 768px and 1366px
	const isTablet = isTouch && screenWidth >= 768 && screenWidth <= 1366;

	// Mobile: touch-enabled device with width less than 768px
	const isMobile = isTouch && screenWidth < 768;

	return {
		isTablet,
		isMobile,
		isTouch,
		screenWidth,
		screenHeight,
	};
};

/**
 * Hook for device detection with resize handling
 */
export const useDeviceDetection = (): DeviceInfo => {
	const [deviceInfo, setDeviceInfo] =
		React.useState<DeviceInfo>(detectDevice());

	React.useEffect(() => {
		const handleResize = () => {
			setDeviceInfo(detectDevice());
		};

		window.addEventListener('resize', handleResize);
		window.addEventListener('orientationchange', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('orientationchange', handleResize);
		};
	}, []);

	return deviceInfo;
};
